import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader, Loading, StatusBadge, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarDays, CalendarPlus, Plus, X, UserCheck, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

// "YYYY-MM-DD" for the local date `daysAhead` days from today — bounds for the comp-off date picker.
function localDate(daysAhead = 0) {
  const d = new Date();
  d.setDate(d.getDate() + daysAhead);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function daysBetween(a, b, half) {
  if (!a || !b) return 0;
  const d = (new Date(b) - new Date(a)) / 86400000 + 1;
  if (d <= 0) return 0;
  return half && d === 1 ? 0.5 : d;
}

export default function MyLeave() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const EMPTY_FORM = { leave_type_code: "", from_date: "", to_date: "", half_day: false, reason: "", backup_employee_id: "" };
  const [form, setForm] = useState(EMPTY_FORM);
  const [reassign, setReassign] = useState(null);   // { id, from_date, to_date, backup_employee_id }
  const EMPTY_COMP_OFF = { work_date: "", reason: "" };
  const [compOffOpen, setCompOffOpen] = useState(false);
  const [compOffForm, setCompOffForm] = useState(EMPTY_COMP_OFF);
  const { data: compOffs } = useQuery({ queryKey: ["myCompOff"], queryFn: async () => (await api.get("/my/comp-off-requests")).data });
  const requestCompOff = useMutation({
    mutationFn: async () => (await api.post("/my/comp-off-requests", compOffForm)).data,
    onSuccess: (res) => { toast.success(res.status === "Pending_RO" ? "Request sent to your reporting officer" : "Request sent to HR"); qc.invalidateQueries({ queryKey: ["myCompOff"] }); setCompOffOpen(false); setCompOffForm(EMPTY_COMP_OFF); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const cancelCompOff = useMutation({
    mutationFn: async (cid) => (await api.delete(`/my/comp-off-requests/${cid}`)).data,
    onSuccess: () => { toast.success("Comp-off request cancelled"); qc.invalidateQueries({ queryKey: ["myCompOff"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const { data, isLoading } = useQuery({ queryKey: ["myLeave"], queryFn: async () => (await api.get("/my/leave")).data });
  const isRH = form.leave_type_code === "RH";
  // Restricted holiday: the employee picks from the HR-published restricted holiday list (max 2 per calendar year).
  const { data: rh } = useQuery({
    queryKey: ["myRestrictedHolidays"], enabled: isRH,
    queryFn: async () => (await api.get("/my/restricted-holidays")).data,
  });
  const days = isRH ? (form.from_date ? 1 : 0) : daysBetween(form.from_date, form.to_date, form.half_day);
  const { data: backupInfo } = useQuery({
    queryKey: ["backupCandidates", form.from_date, form.to_date],
    queryFn: async () => (await api.get("/my/leave/backup-candidates", { params: { from_date: form.from_date, to_date: form.to_date } })).data,
    enabled: open,
  });
  const { data: reassignInfo } = useQuery({
    queryKey: ["backupCandidates", reassign?.from_date, reassign?.to_date],
    queryFn: async () => (await api.get("/my/leave/backup-candidates", { params: { from_date: reassign.from_date, to_date: reassign.to_date } })).data,
    enabled: !!reassign,
  });
  const { data: backupRequests } = useQuery({ queryKey: ["myBackupRequests"], queryFn: async () => (await api.get("/my/backup-requests")).data });
  const backupRequired = !!backupInfo?.required;
  const decideBackup = useMutation({
    mutationFn: async ({ id, action }) => (await api.post(`/my/backup-requests/${id}/decide`, { action, remarks: "" })).data,
    onSuccess: (res) => { toast.success(res.backup_status === "Accepted" ? "You are the backup for these dates" : "Backup request declined"); qc.invalidateQueries({ queryKey: ["myBackupRequests"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const doReassign = useMutation({
    mutationFn: async () => (await api.post(`/my/leave/${reassign.id}/backup`, { backup_employee_id: reassign.backup_employee_id })).data,
    onSuccess: (res) => { toast.success(`Backup request sent to ${res.backup_name}`); setReassign(null); qc.invalidateQueries({ queryKey: ["myLeave"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const apply = useMutation({
    mutationFn: async () => (await api.post("/my/leave", { ...form, days, backup_employee_id: form.backup_employee_id || null })).data,
    onSuccess: (res) => {
      if (res.cascaded) {
        const parts = res.segments.map((s) => `${s.days} day(s) ${s.leave_type_code}`).join(" + ");
        toast.success(`Balance was short — split across ${parts}`, { duration: 6000 });
      } else {
        toast.success("Leave request submitted");
      }
      if (form.backup_employee_id) toast.info("Your backup has been asked to accept. The request goes for approval once they accept.", { duration: 6000 });
      qc.invalidateQueries({ queryKey: ["myLeave"] }); qc.invalidateQueries({ queryKey: ["myDash"] }); setOpen(false); setForm(EMPTY_FORM);
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const cancel = useMutation({
    mutationFn: async (rid) => (await api.delete(`/my/leave/${rid}`)).data,
    onSuccess: () => { toast.success("Request cancelled"); qc.invalidateQueries({ queryKey: ["myLeave"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  if (isLoading || !data) return <Loading />;
  const balances = data.balances || [];
  const selected = balances.find((b) => b.code === form.leave_type_code);

  return (
    <div>
      <PageHeader overline="Time Off" title="My Leave" icon={CalendarDays}
        subtitle="Balances follow the IntenCiv Service Manual (Section 6). Apply and track your requests."
        actions={<div className="flex flex-wrap gap-2">
          <Button variant="outline" className="btn-lift" onClick={() => setCompOffOpen(true)} data-testid="request-comp-off-btn"><CalendarPlus className="h-4 w-4 mr-1" /> Request Comp-Off</Button>
          <Button className="bg-navy hover:bg-navy-700 btn-lift" onClick={() => setOpen(true)} disabled={balances.length === 0} data-testid="apply-leave-btn"><Plus className="h-4 w-4 mr-1" /> Apply for Leave</Button>
        </div>} />

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 stagger" data-testid="leave-balances">
        {balances.map((b) => (
          <Card key={b.code} className="p-5 border-slate-200 shadow-none">
            <div className="flex items-center justify-between">
              <span className="overline text-teal">{b.code}</span>
              {b.encashable && <span className="text-[10px] bg-emerald-50 text-emerald-700 rounded-full px-2 py-0.5">Encashable</span>}
              {b.lwp && <span className="text-[10px] bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">LWP</span>}
            </div>
            <p className={`font-serif-display text-4xl mt-2 ${b.lwp && (b.taken + b.pending) > 0 ? "text-red-600" : "text-navy"}`}>{b.lwp ? ((b.taken + b.pending) > 0 ? `-${b.taken + b.pending}` : "0") : b.available}</p>
            <p className="text-sm text-slate-500">{b.name}</p>
            {b.lwp && <p className="text-xs text-slate-500 mt-1">Unpaid days this year: {b.taken} taken · {b.pending} pending — deducted from salary, not from PL. Max 5 unpaid days per month; more than 5 in a month means no PL for that month.</p>}
            {!b.lwp && <p className="text-xs text-slate-500 mt-1">{b.opening_as_of ? `${b.opening_balance} on ${b.opening_as_of} · ` : ""}Accrued {b.accrued} · Taken {b.taken} · Pending {b.pending}</p>}
            <p className="text-[10px] text-slate-500 mt-1">Ref {b.source_ref}</p>
          </Card>
        ))}
      </div>

      {(backupRequests || []).length > 0 && (
        <div className="mb-6" data-testid="backup-requests">
          <h3 className="font-serif-display text-xl text-navy mb-3">Backup Requests</h3>
          <Card className="border-slate-200 shadow-none overflow-hidden">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="overline text-slate-500">Colleague</TableHead>
                <TableHead className="overline text-slate-500">Dates</TableHead>
                <TableHead className="overline text-slate-500">Your duty starts</TableHead>
                <TableHead className="overline text-slate-500">Status</TableHead>
                <TableHead className="text-right overline text-slate-500"></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {backupRequests.map((b) => (
                  <TableRow key={b.id} data-testid={`backup-req-${b.id}`}>
                    <TableCell className="font-medium text-navy">{b.applicant_name}<span className="block text-xs text-slate-400 font-mono-i">{b.applicant_code}</span></TableCell>
                    <TableCell className="text-slate-600">{b.from_date} → {b.to_date} <span className="text-xs text-slate-400">({b.days} day(s))</span></TableCell>
                    <TableCell className="text-slate-600">{b.applicant_shift_start ? `${b.applicant_shift_start} (their timing)` : "Your normal timing"}</TableCell>
                    <TableCell><StatusBadge status={b.backup_status === "Pending" ? "Pending" : b.backup_status} /></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {b.backup_status === "Pending" && b.leave_status === "Pending_Backup" && (<>
                        <Button size="sm" variant="ghost" className="text-emerald-600" disabled={decideBackup.isPending} onClick={() => decideBackup.mutate({ id: b.id, action: "approve" })} data-testid={`backup-accept-${b.id}`}><CheckCircle2 className="h-4 w-4 mr-1" />Accept</Button>
                        <Button size="sm" variant="ghost" className="text-red-500" disabled={decideBackup.isPending} onClick={() => decideBackup.mutate({ id: b.id, action: "reject" })} data-testid={`backup-reject-${b.id}`}><XCircle className="h-4 w-4 mr-1" />Reject</Button>
                      </>)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <p className="text-xs text-slate-500 mt-2">On a day you cover, your duty starts at your colleague's time if it is earlier. Working 4+ hours beyond your normal shift that day earns a comp-off (after manager approval).</p>
        </div>
      )}

      {(compOffs || []).length > 0 && (
        <div className="mb-6" data-testid="my-comp-offs">
          <h3 className="font-serif-display text-xl text-navy mb-3">My Comp-Offs</h3>
          <Card className="border-slate-200 shadow-none overflow-hidden">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="overline text-slate-500">Date</TableHead>
                <TableHead className="overline text-slate-500">Reason</TableHead>
                <TableHead className="overline text-slate-500">Status</TableHead>
                <TableHead className="overline text-slate-500">Use By</TableHead>
                <TableHead className="text-right overline text-slate-500"></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {compOffs.map((c) => {
                  const advance = c.source === "work_request";
                  const missed = c.status === "Work_Approved" && c.work_date < localDate(0);
                  return (
                    <TableRow key={c.id} className="table-row-i" data-testid={`comp-off-${c.id}`}>
                      <TableCell className="text-slate-600 font-mono-i text-sm">{c.work_date}<span className="block text-[10px] text-slate-400 font-sans">{advance ? "Your advance request" : c.reason?.startsWith("Cover duty") ? "Cover duty" : "Automatic (earlier rule)"}</span></TableCell>
                      <TableCell className="text-slate-500 text-sm min-w-[200px] max-w-[340px] whitespace-normal break-words">{c.reason}{c.status === "Rejected" && c.remarks && c.remarks !== "Rejected" ? <span className="block text-xs text-red-500">Remarks: {c.remarks}</span> : null}</TableCell>
                      <TableCell>
                        <StatusBadge status={c.status} />
                        {missed && <span className="block text-[10px] text-amber-600 mt-1">Not earned — 6+ hours weren't recorded that day</span>}
                      </TableCell>
                      <TableCell className="text-slate-600 text-sm">{c.status === "Approved" ? c.expires_at : "—"}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">{advance && ["Pending_RO", "Pending_HR", "Work_Approved"].includes(c.status) && !missed && <Button size="sm" variant="ghost" className="text-red-500" disabled={cancelCompOff.isPending} onClick={() => cancelCompOff.mutate(c.id)} data-testid={`cancel-comp-off-${c.id}`}><X className="h-4 w-4" /></Button>}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
          <p className="text-xs text-slate-500 mt-2">Once your request is approved and you work 6+ hours that day, 1 day is added to your Compensatory Off (COMP) balance. Use it through Apply for Leave before the end of that month (Service Manual 6.6.5).</p>
        </div>
      )}

      <h3 className="font-serif-display text-xl text-navy mb-3">My Requests</h3>
      {(data.requests || []).length === 0 ? (
        <EmptyState icon={CalendarDays} title="No leave requests yet" hint="Apply for leave to see it here." />
      ) : (
        <Card className="border-slate-200 shadow-none overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="overline text-slate-500">Type</TableHead>
              <TableHead className="overline text-slate-500">From → To</TableHead>
              <TableHead className="overline text-slate-500">Days</TableHead>
              <TableHead className="overline text-slate-500">Reason</TableHead>
              <TableHead className="overline text-slate-500">Status</TableHead>
              <TableHead className="text-right overline text-slate-500"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.requests.map((r) => (
                <TableRow key={r.id} className="table-row-i" data-testid={`leave-req-${r.id}`}>
                  <TableCell className="font-medium text-navy">
                    {r.leave_type_code}
                    {r.auto_cascaded && <span className="block text-[10px] text-amber-600 font-normal">Auto-shifted (balance short)</span>}
                    {r.source === "attendance_half_day" && <span className="block text-[10px] text-slate-400 font-normal">From attendance Half Day</span>}
                    {r.backup_name && <span className="block text-[10px] text-teal font-normal">Backup: {r.backup_name} · {r.backup_status}</span>}
                  </TableCell>
                  <TableCell className="text-slate-600">{r.from_date} → {r.to_date}</TableCell>
                  <TableCell className="text-slate-600">{r.days}{r.half_day ? " (½)" : ""}</TableCell>
                  <TableCell className="text-slate-500 text-sm min-w-[200px] max-w-[340px] whitespace-normal break-words">{r.reason}{r.medical_cert_required ? " · med-cert req." : ""}</TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                  <TableCell className="text-right whitespace-nowrap">{r.status === "Backup_Rejected" && <Button size="sm" variant="outline" className="mr-1" onClick={() => setReassign({ id: r.id, from_date: r.from_date, to_date: r.to_date, backup_employee_id: "" })} data-testid={`reassign-${r.id}`}><UserCheck className="h-4 w-4 mr-1" />Choose backup</Button>}{["Pending_Backup", "Backup_Rejected", "Pending_RO", "Pending_HR"].includes(r.status) && <Button size="sm" variant="ghost" className="text-red-500" onClick={() => cancel.mutate(r.id)} data-testid={`cancel-${r.id}`}><X className="h-4 w-4" /></Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="apply-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl text-navy">Apply for Leave</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Leave Type *</Label>
              <Select value={form.leave_type_code} onValueChange={(v) => setForm({ ...form, leave_type_code: v, from_date: "", to_date: "", half_day: false })}>
                <SelectTrigger className="mt-1" data-testid="leave-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent className="bg-white">{balances.map((b) => <SelectItem key={b.code} value={b.code}>{b.name} {b.lwp ? "(LWP)" : `(${b.available} left)`}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {isRH ? (
              <div data-testid="rh-picker"><Label>Restricted holiday *</Label>
                <Select value={form.from_date} onValueChange={(v) => setForm({ ...form, from_date: v, to_date: v, half_day: false })}>
                  <SelectTrigger className="mt-1" data-testid="rh-select"><SelectValue placeholder="Choose from the restricted holiday list" /></SelectTrigger>
                  <SelectContent className="bg-white">{(rh?.holidays || []).map((h) => (
                    <SelectItem key={h.id} value={h.date} disabled={h.taken}>
                      {h.name} · {new Date(h.date).toLocaleDateString(undefined, { day: "numeric", month: "short", weekday: "short" })}{h.taken ? ` — already ${h.status === "Approved" ? "availed" : "applied"}` : ""}
                    </SelectItem>
                  ))}</SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  {rh ? `${rh.used} of ${rh.allowed} restricted holidays used in ${rh.year}; ${rh.remaining} left.` : "Loading the list…"}
                  {rh && rh.holidays.length === 0 ? " HR has not published any restricted holidays for this year yet." : ""}
                </p>
              </div>
            ) : (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>From *</Label><Input type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} className="mt-1" data-testid="leave-from" /></div>
              <div><Label>To *</Label><Input type="date" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} className="mt-1" data-testid="leave-to" /></div>
            </div>
            )}
            {backupRequired && (
              <div data-testid="backup-select-wrap"><Label>Backup / Deputy *</Label>
                <Select value={form.backup_employee_id} onValueChange={(v) => setForm({ ...form, backup_employee_id: v })} disabled={!form.from_date}>
                  <SelectTrigger className="mt-1" data-testid="backup-select"><SelectValue placeholder={form.from_date ? "Choose the colleague who will cover your duty" : "Select dates first"} /></SelectTrigger>
                  <SelectContent className="bg-white">{(backupInfo?.candidates || []).map((c) => (
                    <SelectItem key={c.id} value={c.id} disabled={!c.available}>{c.name}{c.location ? ` · ${c.location}` : ""}{c.available ? "" : ` — ${c.unavailable_reason}`}</SelectItem>
                  ))}</SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">Your backup gets a request to accept. Your leave goes for approval only after they accept.</p>
              </div>
            )}
            {selected?.half_day_allowed && !isRH && (
              <div className="flex items-center gap-2"><Switch checked={form.half_day} onCheckedChange={(v) => setForm({ ...form, half_day: v })} data-testid="leave-half" /><span className="text-sm text-slate-600">Half day (single day only)</span></div>
            )}
            <div className="rounded-md bg-slate-50 border border-slate-100 px-3 py-2 text-sm text-navy">Total: <span className="font-semibold">{days}</span> day(s){selected && selected.requires_medical_cert_after > 0 && days > selected.requires_medical_cert_after ? " · medical certificate required" : ""}</div>
            <div><Label>Reason *</Label><Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1" data-testid="leave-reason" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-navy hover:bg-navy-700" disabled={!form.leave_type_code || !form.from_date || !form.to_date || days <= 0 || form.reason.length < 3 || (backupRequired && !form.backup_employee_id) || apply.isPending} onClick={() => apply.mutate()} data-testid="submit-leave">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={compOffOpen} onOpenChange={setCompOffOpen}>
        <DialogContent className="bg-white" data-testid="comp-off-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl text-navy">Request Comp-Off</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Been asked to work on your weekly off or a holiday? Request it here <span className="font-semibold">before the day</span>. Your reporting officer (or HR) approves it, and after you work 6+ hours that day, 1 comp-off day is added to your balance.</p>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">Coming in on a weekly off or holiday without an approved request does not earn a comp-off.</p>
            <p className="text-xs text-teal-800 bg-teal-50 border border-teal-100 rounded-md px-3 py-2" data-testid="comp-off-payout-note">Take your comp-off within the same month. If you do not, it is paid with that month's salary at double your day's wage (basic salary divided by 30), which is one day's gross salary, so you are paid either way.</p>
            <div><Label>Day you will work *</Label><Input type="date" min={localDate(0)} max={localDate(60)} value={compOffForm.work_date} onChange={(e) => setCompOffForm({ ...compOffForm, work_date: e.target.value })} className="mt-1" data-testid="comp-off-date" />
              <p className="text-xs text-slate-500 mt-1">Your weekly off or a holiday — today or up to 60 days ahead.</p></div>
            <div><Label>Reason *</Label><Textarea value={compOffForm.reason} onChange={(e) => setCompOffForm({ ...compOffForm, reason: e.target.value })} placeholder="Why are you needed that day, and who asked you to come in?" className="mt-1" data-testid="comp-off-reason" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompOffOpen(false)}>Cancel</Button>
            <Button className="bg-navy hover:bg-navy-700" disabled={!compOffForm.work_date || compOffForm.reason.trim().length < 5 || requestCompOff.isPending} onClick={() => requestCompOff.mutate()} data-testid="submit-comp-off">Send request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reassign} onOpenChange={(v) => !v && setReassign(null)}>
        <DialogContent className="bg-white" data-testid="reassign-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl text-navy">Choose Another Backup</DialogTitle></DialogHeader>
          {reassign && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">Leave {reassign.from_date} → {reassign.to_date}. Your previous backup declined.</p>
              <Select value={reassign.backup_employee_id} onValueChange={(v) => setReassign({ ...reassign, backup_employee_id: v })}>
                <SelectTrigger data-testid="reassign-select"><SelectValue placeholder="Choose a colleague" /></SelectTrigger>
                <SelectContent className="bg-white">{(reassignInfo?.candidates || []).map((c) => (
                  <SelectItem key={c.id} value={c.id} disabled={!c.available}>{c.name}{c.location ? ` · ${c.location}` : ""}{c.available ? "" : ` — ${c.unavailable_reason}`}</SelectItem>
                ))}</SelectContent>
              </Select>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassign(null)}>Cancel</Button>
            <Button className="bg-navy hover:bg-navy-700" disabled={!reassign?.backup_employee_id || doReassign.isPending} onClick={() => doReassign.mutate()} data-testid="reassign-submit">Send request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
